interface SerializedElement {
  html: string;
  width: number;
  height: number;
  isHalf: boolean;
  hideHeader: boolean;
  ref?: HTMLElement;
}

type ApiSerializedElement = Omit<SerializedElement, 'isHalf'>;

interface SerializedElements {
  css: string;
  blocks: ApiSerializedElement[];
  is_overview: boolean;
  is_sidebar_open: boolean;
}

export const serializeElement = (target: HTMLElement): SerializedElement => {
  return {
    height: target.clientHeight,
    width: target.clientWidth,
    html: target.outerHTML,
    isHalf: target.getAttribute('span') === '6',
    ref: target,
    hideHeader: target.className.includes('hide-header'),
  };
};

/** Collect all class names that appear in the element's subtree (from class attributes). */
function getClassNamesFromElement(container: HTMLElement): Set<string> {
  const set = new Set<string>();
  const addClasses = (el: Element) => {
    const c = el.getAttribute('class');
    if (c)
      c.trim()
        .split(/\s+/)
        .forEach((cls) => set.add(cls));
  };
  addClasses(container);
  container.querySelectorAll('*').forEach(addClasses);
  return set;
}

/** Union of class names from multiple element subtrees (e.g. section roots only). */
function getClassNamesFromElements(roots: HTMLElement[]): Set<string> {
  const set = new Set<string>();
  roots.forEach((el) =>
    getClassNamesFromElement(el).forEach((cls) => set.add(cls))
  );
  return set;
}

/** Extract class names from a CSS selector (e.g. ".flex.p-2" -> ["flex", "p-2"]). */
function getClassNamesFromSelector(selector: string): string[] {
  const classes: string[] = [];
  const re = /\.([a-zA-Z_][\w-]*)/g;
  let m = re.exec(selector);
  while (m !== null) {
    classes.push(m[1]);
    m = re.exec(selector);
  }
  return classes;
}

/** Max container node count before we skip per-rule querySelector (avoids freeze on large DOM). */
const LARGE_CONTAINER_NODE_THRESHOLD = 400;

/**
 * Returns true if the rule applies to the scope: every class in its selector
 * appears in containerClasses. When useQuerySelector is true, also checks that
 * the selector matches a node in one of the roots (slower; skipped for large DOM).
 */
function ruleMatchesElement(
  rule: CSSRule,
  containerClasses: Set<string>,
  roots: HTMLElement[],
  useQuerySelector: boolean
): boolean {
  if (rule.type !== CSSRule.STYLE_RULE) {
    return true; // keep non-style rules for recursive handling
  }
  const styleRule = rule as CSSStyleRule;
  const selector = styleRule.selectorText?.trim();
  if (!selector) return false;
  if (useQuerySelector && roots.length > 0) {
    try {
      const matchesSomeRoot = roots.some(
        (el) => el.matches(selector) || el.querySelector(selector) != null
      );
      if (!matchesSomeRoot) return false;
    } catch {
      return true; // invalid selector (e.g. :hover) – include to be safe
    }
  }
  const parts = selector.split(',').map((s) => s.trim());
  return parts.some((part) => {
    const selectorClasses = getClassNamesFromSelector(part);
    if (selectorClasses.length === 0) return true; // e.g. div, body
    return selectorClasses.every((cls) => containerClasses.has(cls));
  });
}

function serializeRule(
  rule: CSSRule,
  roots: HTMLElement[],
  containerClasses: Set<string>,
  useQuerySelector: boolean,
  out: string[]
) {
  if (rule.type === CSSRule.STYLE_RULE) {
    if (
      roots.length === 0 ||
      ruleMatchesElement(rule, containerClasses, roots, useQuerySelector)
    ) {
      out.push(rule.cssText);
    }
    return;
  }
  if (rule.type === CSSRule.MEDIA_RULE) {
    const mediaRule = rule as CSSMediaRule;
    const inner: string[] = [];
    Array.from(mediaRule.cssRules).forEach((r) =>
      serializeRule(r, roots, containerClasses, useQuerySelector, inner)
    );
    if (inner.length > 0) {
      out.push(`@media ${mediaRule.conditionText} { ${inner.join(' ')} }`);
    }
    return;
  }
  // @font-face, @keyframes, etc. – include in full
  out.push(rule.cssText);
}

/** Single element or array of section roots to scope CSS to (no tab chrome). */
export type CssScope = HTMLElement | HTMLElement[] | null | undefined;

function toRoots(scope: CssScope): HTMLElement[] {
  if (scope == null) return [];
  return Array.isArray(scope) ? scope : [scope];
}

/**
 * Serialize CSS for export. If scope is provided (single element or array of
 * section roots), only includes rules that match those subtrees. Pass the
 * exportable section roots so we don't process the whole tab.
 */
export const serializeCss = (scope?: CssScope): string => {
  const styles = [...document.styleSheets];
  const out: string[] = [];
  const roots = toRoots(scope);
  const containerClasses =
    roots.length > 0 ? getClassNamesFromElements(roots) : new Set<string>();
  const nodeCount =
    roots.length > 0
      ? roots.reduce((n, el) => n + el.getElementsByTagName('*').length, 0)
      : 0;
  const useQuerySelector = nodeCount <= LARGE_CONTAINER_NODE_THRESHOLD;
  styles.forEach((style) => {
    try {
      if (style?.cssRules) {
        Array.from(style.cssRules).forEach((rule) =>
          serializeRule(rule, roots, containerClasses, useQuerySelector, out)
        );
      }
    } catch (e) {
      // Ignore stylesheets we can't access (e.g. cross-origin)
    }
  });
  let css = out.join(' ');
  css += ' .see-more-details { display: none; } ';
  const staticPrefix = window.location.origin.includes('localhost')
    ? 'https://develop-discovery-us.helixa.ai'
    : window.location.origin;
  css = css.replace(/\/static\//g, `${staticPrefix}/static/`);
  return css;
};

export const joinBlocks = (
  initialBlocks: SerializedElement[],
  joinedBlocks: ApiSerializedElement[],
  block: SerializedElement,
  currentIndex: number
): ApiSerializedElement[] => {
  const nextBlock = initialBlocks[currentIndex + 1];
  const gap = 5;
  if (block.isHalf && nextBlock?.isHalf) {
    const joinedBlock: ApiSerializedElement = {
      ...block,
      height: block.height,
      width: block.width + nextBlock.width + gap,
      html: `<div style="display: flex; gap: ${gap}px; flex: 1;">${block.html}${nextBlock.html}</div>`,
    };
    delete initialBlocks[currentIndex + 1];
    return joinedBlocks.concat(joinedBlock);
  }

  const { isHalf, ref, ...apiBlock } = block;
  return joinedBlocks.concat(apiBlock);
};

export const serializeElements = (
  targets: HTMLElement[],
  isOverview: boolean,
  isSideBarOpen: boolean,
  cssScope?: CssScope,
  precomputedCss?: string
): SerializedElements => {
  const blocks = targets.map(serializeElement);
  const joinedBlocks: ApiSerializedElement[] = blocks.reduce(
    (acc: ApiSerializedElement[], block, currentIndex) => {
      return joinBlocks(blocks, acc, block, currentIndex);
    },
    []
  );
  const css = precomputedCss ?? serializeCss(cssScope);
  return {
    css,
    blocks: joinedBlocks,
    is_overview: isOverview,
    is_sidebar_open: isSideBarOpen,
  };
};
